import { store } from "../core/store";
import { getForgetfulnessTiming } from "../../shared/cccEffects";
import { socket } from "../core/socket";
import { serverNow } from "../core/clockSync";
import { canvasBus } from "../core/canvasBus";
import { CanvasRenderer } from "./canvas/CanvasRenderer";
import { CanvasEditor, type ActiveTool } from "./canvas/CanvasEditor";
import { StrokeBatcher } from "./canvas/StrokeBatcher";
import { CANVAS_COLORS } from "../../shared/palette";
import type { CCCBlindnessCycle, CCCCardView } from "../../shared/types";
import { buildCCCNameHover } from "./cccTooltip";

const BRUSH_SIZES = [3, 8, 16, 30];

function computePixelLevel(activeCCCs: CCCCardView[]): 0 | 1 | 2 | 3 {
  const entry = activeCCCs.find((c) => c.familyId === "pixelated");
  if (!entry) return 0;
  if (entry.level >= 3) return 3;
  return entry.level >= 2 ? 2 : 1;
}

function computeMonochromeLevel(activeCCCs: CCCCardView[]): 0 | 1 | 2 {
  const entry = activeCCCs.find((c) => c.familyId === "monochrome");
  if (!entry) return 0;
  return entry.level >= 2 ? 2 : 1;
}

function computeForgetfulness(activeCCCs: CCCCardView[]): { delayMs: number; fadeMs: number } | null {
  const entry = activeCCCs.find((c) => c.familyId === "forgetfulness");
  if (!entry) return null;
  return getForgetfulnessTiming(entry.level);
}

function computeLag(activeCCCs: CCCCardView[]): { delayMs: number } | null {
  const entry = activeCCCs.find((c) => c.familyId === "lag");
  if (!entry) return null;
  return { delayMs: entry.level >= 2 ? 5_000 : 3_000 };
}

function computeTrembleLevel(activeCCCs: CCCCardView[]): 0 | 1 | 2 | 3 {
  const entry = activeCCCs.find((c) => c.familyId === "tremble");
  if (!entry) return 0;
  if (entry.level >= 3) return 3;
  return entry.level >= 2 ? 2 : 1;
}

function computeClaustrophobiaLevel(activeCCCs: CCCCardView[]): 0 | 1 | 2 {
  const entry = activeCCCs.find((c) => c.familyId === "claustrophobia");
  if (!entry) return 0;
  return entry.level >= 2 ? 2 : 1;
}

function computeConfusionLevel(activeCCCs: CCCCardView[]): 0 | 1 | 2 {
  const entry = activeCCCs.find((c) => c.familyId === "confusion");
  if (!entry) return 0;
  return entry.level >= 2 ? 2 : 1;
}

/** Upside Down only changes what OTHER players see - the artist always sees and
 * interacts with their own canvas normally, per the card's own description. */
function computeUpsideDownTransform(activeCCCs: CCCCardView[], isArtist: boolean): string | null {
  if (isArtist) return null;
  const entry = activeCCCs.find((c) => c.familyId === "upsideDown");
  if (!entry) return null;
  return entry.level >= 2 ? "scaleY(-1) scaleX(-1)" : "scaleY(-1)";
}

/** Hazy is the mirror image of Upside Down: it only ever changes what the
 * ARTIST sees - everyone else's canvas is completely unaffected, per the
 * card's own description. A pure CSS `filter: blur(...)` on the canvas element
 * itself, so it's purely visual (never touches actual pixel data) and never
 * bleeds onto the toolbar, which is a sibling element, not a descendant. */
function computeHazyBlurPx(activeCCCs: CCCCardView[], isArtist: boolean): number {
  if (!isArtist) return 0;
  const entry = activeCCCs.find((c) => c.familyId === "hazy");
  if (!entry) return 0;
  if (entry.level >= 3) return 20;
  return entry.level >= 2 ? 10 : 5;
}

export function mountCanvasView(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "canvas-view";

  const snapshot = store.getState().snapshot;
  const isArtist = !!snapshot && snapshot.currentArtistId === store.getState().myPlayerId;
  const activeCCCs = snapshot?.activeArtistCCCs ?? [];

  if (activeCCCs.length > 0) {
    const statusLine = document.createElement("div");
    statusLine.className = "ccc-active-status";
    statusLine.appendChild(document.createTextNode("Active: "));
    activeCCCs.forEach((card, i) => {
      if (i > 0) statusLine.appendChild(document.createTextNode(", "));
      statusLine.appendChild(buildCCCNameHover(card));
    });
    wrap.appendChild(statusLine);
  }

  const canvasWrap = document.createElement("div");
  canvasWrap.className = "canvas-wrap";
  const canvasEl = document.createElement("canvas");
  canvasEl.className = "drawing-canvas";
  const upsideDownTransform = computeUpsideDownTransform(activeCCCs, isArtist);
  if (upsideDownTransform) canvasEl.style.transform = upsideDownTransform;
  const hazyBlurPx = computeHazyBlurPx(activeCCCs, isArtist);
  if (hazyBlurPx > 0) canvasEl.style.filter = `blur(${hazyBlurPx}px)`;
  canvasWrap.appendChild(canvasEl);

  const blindnessOverlay = document.createElement("div");
  blindnessOverlay.className = "ccc-blindness-overlay";
  canvasWrap.appendChild(blindnessOverlay);

  const renderer = new CanvasRenderer(canvasEl);
  renderer.setEffects({
    pixelLevel: computePixelLevel(activeCCCs),
    monochromeLevel: computeMonochromeLevel(activeCCCs),
    forgetfulness: computeForgetfulness(activeCCCs),
    lag: computeLag(activeCCCs),
  });
  const oneLineManActive = activeCCCs.some((c) => c.familyId === "oneLineMan");
  const trembleLevel = computeTrembleLevel(activeCCCs);
  const claustrophobiaLevel = computeClaustrophobiaLevel(activeCCCs);
  const confusionLevel = computeConfusionLevel(activeCCCs);
  const strokeBatcher = new StrokeBatcher((event) => socket.emit("drawing:event", event));
  const editor = new CanvasEditor(
    canvasEl,
    renderer,
    (event) => strokeBatcher.push(event),
    oneLineManActive,
    trembleLevel,
    claustrophobiaLevel,
    confusionLevel
  );

  const unsubSync = canvasBus.onSync((items) => renderer.syncFromServer(items));
  const unsubEvent = canvasBus.onEvent((event) => renderer.applyServerEvent(event));
  renderer.syncFromServer(canvasBus.getLastSync());

  // "Blindness": a shared, server-timed fade-to-black cycle - every viewer (artist
  // included) animates from the same startAt timestamp so the blackout is in sync.
  let currentBlindnessCycle: CCCBlindnessCycle | null = null;
  let blindnessRafId: number | null = null;
  function tickBlindness(): void {
    const cycle = currentBlindnessCycle;
    if (!cycle) {
      blindnessOverlay.style.opacity = "0";
      blindnessRafId = null;
      return;
    }
    const elapsed = serverNow() - cycle.startAt;
    let opacity: number;
    if (elapsed < 0) {
      opacity = 0;
    } else if (elapsed < cycle.fadeOutMs) {
      opacity = elapsed / cycle.fadeOutMs;
    } else if (elapsed < cycle.fadeOutMs + cycle.holdMs) {
      opacity = 1;
    } else if (elapsed < cycle.fadeOutMs + cycle.holdMs + cycle.fadeInMs) {
      opacity = 1 - (elapsed - cycle.fadeOutMs - cycle.holdMs) / cycle.fadeInMs;
    } else {
      opacity = 0;
      currentBlindnessCycle = null;
    }
    blindnessOverlay.style.opacity = String(opacity);
    blindnessRafId = requestAnimationFrame(tickBlindness);
  }
  const unsubBlindness = canvasBus.onBlindness((cycle) => {
    currentBlindnessCycle = cycle;
    if (blindnessRafId === null) blindnessRafId = requestAnimationFrame(tickBlindness);
  });

  wrap.appendChild(canvasWrap);
  let teardownHotkeys: (() => void) | null = null;
  if (isArtist) {
    const restrictions = {
      noReturn: activeCCCs.some((c) => c.familyId === "noReturn"),
      crossingTheLine: activeCCCs.some((c) => c.familyId === "crossingTheLine"),
      monochromeII: activeCCCs.some((c) => c.familyId === "monochrome" && c.level >= 2),
    };
    const toolbar = buildToolbar(editor, restrictions);
    wrap.appendChild(toolbar.element);
    editor.setEnabled(true);
    teardownHotkeys = toolbar.destroy;
  } else {
    editor.setEnabled(false);
  }

  container.appendChild(wrap);

  return () => {
    strokeBatcher.flush();
    unsubSync();
    unsubEvent();
    unsubBlindness();
    if (blindnessRafId !== null) cancelAnimationFrame(blindnessRafId);
    renderer.destroy();
    teardownHotkeys?.();
  };
}

interface ToolbarRestrictions {
  /** "No Return" - disables eraser, undo, redo, and clear (and their hotkeys). */
  noReturn: boolean;
  /** "Crossing the Line" - disables pen and fill (and their hotkeys). */
  crossingTheLine: boolean;
  /** "Monochrome II" - only black/white are selectable (avoids the thresholding
   * artifacts other colors produced at render time - see ARCHITECTURE.md). */
  monochromeII: boolean;
}

interface ToolbarResult {
  element: HTMLElement;
  destroy: () => void;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

function buildToolbar(editor: CanvasEditor, restrictions: ToolbarRestrictions): ToolbarResult {
  const toolbar = document.createElement("div");
  toolbar.className = "canvas-toolbar";

  const toolsRow = document.createElement("div");
  toolsRow.className = "toolbar-row";
  const tools: { tool: ActiveTool; label: string; disabled?: boolean }[] = [
    { tool: "pen", label: "✏ Pen", disabled: restrictions.crossingTheLine },
    { tool: "line", label: "／ Line" },
    { tool: "eraser", label: "🧽 Eraser", disabled: restrictions.noReturn },
    { tool: "bucket", label: "🪣 Fill", disabled: restrictions.crossingTheLine },
  ];
  const toolButtons: HTMLButtonElement[] = [];
  for (const { tool, label, disabled } of tools) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = label;
    btn.className = "tool-button";
    btn.disabled = !!disabled;
    if (tool === "pen" && !disabled) btn.classList.add("active");
    btn.addEventListener("click", () => {
      editor.setTool(tool);
      for (const b of toolButtons) b.classList.remove("active");
      btn.classList.add("active");
    });
    toolButtons.push(btn);
    toolsRow.appendChild(btn);
  }
  // If pen starts disabled (Crossing the Line), default to line instead so the
  // artist isn't left with no tool selected/highlighted.
  if (restrictions.crossingTheLine) {
    editor.setTool("line");
    toolButtons[1]!.classList.add("active");
  }

  const undoBtn = document.createElement("button");
  undoBtn.type = "button";
  undoBtn.className = "tool-button";
  undoBtn.textContent = "↶ Undo";
  undoBtn.disabled = restrictions.noReturn;
  undoBtn.addEventListener("click", () => editor.undo());
  toolsRow.appendChild(undoBtn);

  const redoBtn = document.createElement("button");
  redoBtn.type = "button";
  redoBtn.className = "tool-button";
  redoBtn.textContent = "↷ Redo";
  redoBtn.disabled = restrictions.noReturn;
  redoBtn.addEventListener("click", () => editor.redo());
  toolsRow.appendChild(redoBtn);

  const clearBtn = document.createElement("button");
  clearBtn.type = "button";
  clearBtn.className = "tool-button";
  clearBtn.textContent = "🗑 Clear";
  clearBtn.disabled = restrictions.noReturn;
  clearBtn.addEventListener("click", () => {
    if (confirm("Clear the entire canvas?")) editor.clear();
  });
  toolsRow.appendChild(clearBtn);

  const sizeRow = document.createElement("div");
  sizeRow.className = "toolbar-row";
  const sizeLabel = document.createElement("span");
  sizeLabel.className = "toolbar-label";
  sizeLabel.textContent = "Brush:";
  sizeRow.appendChild(sizeLabel);
  const sizeButtons: HTMLButtonElement[] = [];
  BRUSH_SIZES.forEach((size, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "brush-size-button";
    if (i === 1) btn.classList.add("active");
    const dot = document.createElement("span");
    dot.className = "brush-size-dot";
    dot.style.width = `${Math.min(size, 24)}px`;
    dot.style.height = `${Math.min(size, 24)}px`;
    btn.appendChild(dot);
    btn.addEventListener("click", () => {
      editor.setBrushSize(size);
      for (const b of sizeButtons) b.classList.remove("active");
      btn.classList.add("active");
    });
    sizeButtons.push(btn);
    sizeRow.appendChild(btn);
  });
  editor.setBrushSize(BRUSH_SIZES[1]!);

  const colorRow = document.createElement("div");
  colorRow.className = "toolbar-row color-row";
  const colorButtons: HTMLButtonElement[] = [];
  for (const color of CANVAS_COLORS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "color-swatch-button";
    btn.style.backgroundColor = color;
    btn.disabled = restrictions.monochromeII && color !== "#000000" && color !== "#ffffff";
    if (color === "#000000") btn.classList.add("active");
    btn.addEventListener("click", () => {
      editor.setColor(color);
      for (const b of colorButtons) b.classList.remove("active");
      btn.classList.add("active");
      if (editor.getTool() === "eraser") {
        // Prefer pen, but fall back to line if pen is disabled (Crossing the Line).
        const fallbackIndex = restrictions.crossingTheLine ? 1 : 0;
        editor.setTool(restrictions.crossingTheLine ? "line" : "pen");
        for (const b of toolButtons) b.classList.remove("active");
        toolButtons[fallbackIndex]!.classList.add("active");
      }
    });
    colorButtons.push(btn);
    colorRow.appendChild(btn);
  }
  editor.setColor("#000000");

  toolbar.append(toolsRow, sizeRow, colorRow);

  // Hotkeys, active only while this toolbar (i.e. this player as artist) is mounted.
  // Ctrl/Cmd+<key> combos other than undo/redo are left alone so browser shortcuts
  // (Ctrl+P, Ctrl+F, ...) keep working normally.
  const handleKeydown = (e: KeyboardEvent): void => {
    if (isTypingTarget(e.target)) return;

    if (e.ctrlKey || e.metaKey) {
      const key = e.key.toLowerCase();
      if (key === "z") {
        e.preventDefault();
        undoBtn.click();
      } else if (key === "y") {
        e.preventDefault();
        redoBtn.click();
      }
      return;
    }

    switch (e.key.toLowerCase()) {
      case "1":
        sizeButtons[0]!.click();
        break;
      case "2":
        sizeButtons[1]!.click();
        break;
      case "3":
        sizeButtons[2]!.click();
        break;
      case "4":
        sizeButtons[3]!.click();
        break;
      case "q":
      case "p":
        toolButtons[0]!.click(); // pen
        break;
      case "w":
      case "l":
        toolButtons[1]!.click(); // line
        break;
      case "e":
        toolButtons[2]!.click(); // eraser
        break;
      case "r":
      case "f":
        toolButtons[3]!.click(); // bucket/fill
        break;
    }
  };
  window.addEventListener("keydown", handleKeydown);

  return {
    element: toolbar,
    destroy: () => window.removeEventListener("keydown", handleKeydown),
  };
}
